import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  FileText, 
  Brain, 
  Copy, 
  Eye, 
  X, 
  ChevronDown,
  ChevronRight,
  Sparkles,
  Zap,
  Lightbulb,
  Package,
  Tag,
  Globe,
  AlertTriangle,
  Calendar,
  User
} from 'lucide-react';
import ClauseService from '../../services/clauseService';
import AIService from '../../services/aiService';
import { useToast } from '../common/Toast';

const ClauseSelector = ({ 
  onClauseSelect, 
  onClauseInsert, 
  contractType = '', 
  contractContext = {},
  selectedClauses = [],
  onSelectedClausesChange 
}) => {
  const { success, error: showError, info, toasts, removeToast } = useToast();
  
  // State management
  const [clauses, setClauses] = useState([]);
  const [filteredClauses, setFilteredClauses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRisk, setSelectedRisk] = useState('');
  const [showAIGeneration, setShowAIGeneration] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiGeneratedClauses, setAiGeneratedClauses] = useState([]);
  const [expandedClause, setExpandedClause] = useState(null);
  const [viewMode, setViewMode] = useState('library'); // 'library', 'ai', 'templates'
  
  // Master data
  const [masterData, setMasterData] = useState({
    categories: [],
    riskLevels: [],
    jurisdictions: [],
    languages: []
  });

  // Load clauses and master data
  useEffect(() => {
    loadClauses();
    loadMasterData();
  }, []);

  // Filter clauses based on search and filters
  useEffect(() => {
    let filtered = clauses;
    
    if (searchTerm) {
      filtered = filtered.filter(clause => 
        clause.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clause.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clause.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(clause => clause.category === selectedCategory);
    }
    
    if (selectedRisk) {
      filtered = filtered.filter(clause => clause.riskLevel === selectedRisk);
    }
    
    setFilteredClauses(filtered);
  }, [clauses, searchTerm, selectedCategory, selectedRisk]);

  const loadClauses = async () => {
    try {
      setLoading(true);
      const response = await ClauseService.getClauses({ limit: 1000 });
      
      if (response && response.success && response.data) {
        setClauses(response.data);
      } else {
        setClauses([]);
      }
    } catch (error) {
      console.error('Error loading clauses:', error);
      showError('Failed to load clause library');
      setClauses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMasterData = async () => {
    try {
      const response = await ClauseService.getMasterData();
      if (response && response.success && response.data) {
        setMasterData(response.data);
      }
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const handleClauseSelect = (clause) => {
    if (onClauseSelect) {
      onClauseSelect(clause);
    }
    
    // Add to selected clauses if not already selected
    const isAlreadySelected = selectedClauses.some(selected => selected._id === clause._id);
    if (!isAlreadySelected && onSelectedClausesChange) {
      onSelectedClausesChange([...selectedClauses, clause]);
    }
    
    success(`Clause "${clause.title}" selected`);
  };

  const handleClauseInsert = (clause) => {
    if (onClauseInsert) {
      onClauseInsert(clause);
    }
    success(`Clause "${clause.title}" inserted into contract`);
  };

  const handleRemoveSelected = (clauseId) => {
    if (onSelectedClausesChange) {
      const updated = selectedClauses.filter(clause => clause._id !== clauseId);
      onSelectedClausesChange(updated);
    }
  };

  const generateAIClause = async () => {
    if (!aiPrompt.trim()) {
      showError('Please enter a prompt for AI clause generation');
      return;
    }

    try {
      setIsGeneratingAI(true);
      setAiGeneratedClauses([]);
      
      const context = {
        contractType,
        ...contractContext,
        prompt: aiPrompt
      };
      
      const response = await AIService.generateClause(context);
      
      if (response && response.success && response.data) {
        setAiGeneratedClauses(response.data);
        success('AI clauses generated successfully');
      } else {
        showError('Failed to generate AI clauses');
      }
    } catch (error) {
      console.error('Error generating AI clauses:', error);
      showError('Failed to generate AI clauses: ' + error.message);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleAIClauseSelect = (clause) => {
    // Add AI-generated clause to library and select it
    const newClause = {
      ...clause,
      _id: `ai_${Date.now()}`,
      isAIGenerated: true,
      source: 'ai',
      createdAt: new Date().toISOString()
    };
    
    handleClauseSelect(newClause);
  };

  const getRiskColor = (riskLevel) => {
    const colors = {
      'LOW': '#10b981',
      'MEDIUM': '#f59e0b',
      'HIGH': '#ef4444',
      'CRITICAL': '#dc2626'
    };
    return colors[riskLevel] || '#6b7280';
  };

  const renderClauseCard = (clause, isAIGenerated = false) => {
    const isSelected = selectedClauses.some(selected => selected._id === clause._id);
    const isExpanded = expandedClause === clause._id;
    
    return (
      <div
        key={clause._id}
        style={{
          border: isSelected ? '2px solid #007B8F' : '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '12px',
          backgroundColor: isSelected ? '#f0f9ff' : 'white',
          transition: 'all 0.2s ease',
          cursor: 'pointer'
        }}
        onClick={() => setExpandedClause(isExpanded ? null : clause._id)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FileText size={16} style={{ color: '#007B8F' }} />
              <h4 style={{ 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: '600',
                color: '#1f2937'
              }}>
                {clause.title}
              </h4>
              {isAIGenerated && (
                <span style={{
                  backgroundColor: '#fef3c7',
                  color: '#d97706',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '500'
                }}>
                  AI
                </span>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <span style={{
                backgroundColor: '#e5e7eb',
                color: '#374151',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                {clause.category}
              </span>
              <span style={{
                backgroundColor: getRiskColor(clause.riskLevel),
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                {clause.riskLevel}
              </span>
            </div>
            
            {isExpanded && (
              <div style={{ 
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                fontSize: '14px',
                lineHeight: '1.5',
                color: '#374151'
              }}>
                {clause.content}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '4px', marginLeft: '12px' }}>
            {isExpanded ? (
              <ChevronDown size={16} style={{ color: '#6b7280' }} />
            ) : (
              <ChevronRight size={16} style={{ color: '#6b7280' }} />
            )}
          </div>
        </div>
        
        {isExpanded && (
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClauseSelect(clause);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: isSelected ? '#007B8F' : '#f3f4f6',
                color: isSelected ? 'white' : '#374151',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <Plus size={14} />
              {isSelected ? 'Selected' : 'Select'}
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClauseInsert(clause);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <Copy size={14} />
              Insert
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '20px', 
          fontWeight: '600',
          color: '#1f2937'
        }}>
          Clause Library
        </h3>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setViewMode('library')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'library' ? '#007B8F' : '#f3f4f6',
              color: viewMode === 'library' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Library
          </button>
          <button
            onClick={() => setViewMode('ai')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'ai' ? '#007B8F' : '#f3f4f6',
              color: viewMode === 'ai' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <Brain size={16} style={{ marginRight: '6px' }} />
            AI Generate
          </button>
        </div>
      </div>

      {/* Selected Clauses Summary */}
      {selectedClauses.length > 0 && (
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #007B8F',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#007B8F' }}>
            Selected Clauses ({selectedClauses.length})
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedClauses.map(clause => (
              <div
                key={clause._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: 'white',
                  border: '1px solid #007B8F',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '12px'
                }}
              >
                <FileText size={14} style={{ color: '#007B8F' }} />
                <span>{clause.title}</span>
                <button
                  onClick={() => handleRemoveSelected(clause._id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: '#6b7280'
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'library' && (
        <>
          {/* Search and Filters */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search 
                size={16} 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }} 
              />
              <input
                type="text"
                placeholder="Search clauses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="">All Categories</option>
              {masterData.categories.map(cat => (
                <option key={cat.code} value={cat.code}>{cat.label}</option>
              ))}
            </select>
            
            <select
              value={selectedRisk}
              onChange={(e) => setSelectedRisk(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              <option value="">All Risk Levels</option>
              {masterData.riskLevels.map(risk => (
                <option key={risk.code} value={risk.code}>{risk.label}</option>
              ))}
            </select>
          </div>

          {/* Clauses List */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                Loading clauses...
              </div>
            ) : filteredClauses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No clauses found matching your criteria</p>
              </div>
            ) : (
              filteredClauses.map(clause => renderClauseCard(clause))
            )}
          </div>
        </>
      )}

      {viewMode === 'ai' && (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#374151'
            }}>
              Describe the clause you need:
            </label>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g., Payment terms for software licensing agreement with 30-day payment terms and late fees..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>
          
          <button
            onClick={generateAIClause}
            disabled={isGeneratingAI || !aiPrompt.trim()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: isGeneratingAI ? '#9ca3af' : '#007B8F',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isGeneratingAI ? 'not-allowed' : 'pointer',
              marginBottom: '20px'
            }}
          >
            {isGeneratingAI ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Generating...
              </>
            ) : (
              <>
                <Brain size={16} />
                Generate AI Clauses
              </>
            )}
          </button>
          
          {aiGeneratedClauses && aiGeneratedClauses.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1f2937' }}>
                Generated Clauses
              </h4>
              {aiGeneratedClauses.map((clause, index) => renderClauseCard(clause, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClauseSelector;
